'use client'

import type { CmsDropdownOption } from '@/utils/getGlobalChrome'
import type { ReactNode } from 'react'
import {
  businessProduct,
  personalProduct,
  preferredBranches,
  preferredContactTimes,
  preferredReachMethods,
  salutations,
  sourceOfEnquiry
} from '@/constants/dropdown'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
// oxlint-disable-next-line no-restricted-imports -- grandfathered; audit before touching (stuck branches can be load-bearing)
// removed MUI Select/MenuItem — use native input for contact number only
import { sendGAEvent } from '@next/third-parties/google'
import parse from 'html-react-parser'
import { useRouter } from 'next/navigation'

import { useBranches, useEnquiryFormOptions } from '@/app/GlobalChromeContext'
import { PrimaryButton } from '@/components/buttons'
import Link from '@/components/Link'
import useStore from '@/stores/Store'
import { submitForm } from '@/utils/service'

import './EnquiryForm.css'

import type { SvgIconProps } from '@mui/material/SvgIcon'

import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Image from 'next/image'

import arrowndown from '@/assets/images/arrow-down.svg'

type DropdownOption = { text: string; value: string; disabled: boolean }

const fromCms = (options?: CmsDropdownOption[] | null): DropdownOption[] | null =>
  options && options.length > 0
    ? options.map(option => ({
        text: option.label ?? '',
        value: option.value ?? '',
        disabled: Boolean(option.isGroupHeader)
      }))
    : null

const CustomArrow = ({ className }: SvgIconProps) => {
  return <Image src={arrowndown} alt="" width={18} height={18} className={className} />
}

const INIT_FORM_DATA = {
  salutation: '',
  full_name: '',
  contact_code: '',
  contact_number: '',
  email: '',
  interested_product: '',
  source_of_enquiry: '',
  preferred_branch: '',
  preferred_contact_time: '',
  preferred_reach_method: '',
  comments: '',
  marketing_consent: false,
  ex_customer: 'no',
  agree: false,
  timestamp: ''
}

interface EnquiryFormProps {
  title?: ReactNode
  description?: ReactNode
}

const EnquiryForm = ({ title = "Let's Connect", description = '' }: EnquiryFormProps) => {
  const [_, setIsError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [formData, setFormData] = useState(INIT_FORM_DATA)
  const [isRecaptchaVerified, setIsRecaptchaVerified] = useState(false)
  const [isConfirm, setIsConfirm] = useState(false)
  const [validationMessage, setValidationMessage] = useState('')
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const ref = useRef(null)
  const { tab, setFormRef } = useStore()
  const cmsOptions = useEnquiryFormOptions()
  const cmsBranches = useBranches()
  const cmsPersonal = fromCms(cmsOptions?.personalProducts)
  const cmsBusiness = fromCms(cmsOptions?.businessProducts)
  const cmsSource = fromCms(cmsOptions?.sourceOfEnquiry)
  const salutationOptions = fromCms(cmsOptions?.salutations) ?? salutations
  const contactTimeOptions = fromCms(cmsOptions?.preferredContactTimes) ?? preferredContactTimes
  const reachMethodOptions = fromCms(cmsOptions?.preferredReachMethods) ?? preferredReachMethods
  const personalOptions = cmsPersonal ? [personalProduct[0], ...cmsPersonal] : personalProduct
  const businessOptions = cmsBusiness ? [businessProduct[0], ...cmsBusiness] : businessProduct
  const sourceOptions = cmsSource ? [sourceOfEnquiry[0], ...cmsSource] : sourceOfEnquiry
  const branchOptions =
    cmsBranches.length > 0
      ? cmsBranches.filter(branch => branch.type !== 'ATM').map(branch => branch.name ?? '')
      : preferredBranches
  const router = useRouter()

  const handleChange = e => {
    const { name, value, type, checked } = e.target

    if (name === 'contact_number') {
      if (/^\d*$/.test(value)) {
        setFormData({
          ...formData,
          [name]: value
        })
      }
    } else if (name === 'full_name') {
      if (/^[a-zA-Z\s]*$/.test(value)) {
        setFormData({
          ...formData,
          [name]: value
        })
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      })
    }
  }
  const sendFormStartGA = () => {
    sendGAEvent({
      event: 'form_start',
      action: 'Start inserting form details',
      label: window.location.pathname + window.location.search
    })
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setValidationMessage('') // Reset validation message

    // Validate required fields
    if (!formData.salutation) {
      setValidationMessage('Please select a salutation.')
      return
    }
    if (!formData.full_name) {
      setValidationMessage('Please enter your full name.')
      return
    }
    // if (!formData.contact_code) {
    //   setValidationMessage("Please enter your contact code.");
    //   return;
    // }
    // if (!formData.contact_number) {
    //  setValidationMessage('Please enter your contact number.')
    //  return
    // }
    if (!formData.email) {
      setValidationMessage('Please enter your email.')
      return
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setValidationMessage('Please enter a valid email address.')
      return
    }
    if (!formData.preferred_branch) {
      setValidationMessage('Please select a preferred branch and Smart Financial Center.')
      return
    }
    if (!formData.interested_product) {
      setValidationMessage('Please specify the interested product.')
      return
    }
    if (!formData.preferred_contact_time) {
      setValidationMessage('Please specify when you prefer to be contacted.')
      return
    }
    if (!formData.preferred_reach_method) {
      setValidationMessage('Please specify how you prefer to be reached.')
      return
    }
    if (!formData.source_of_enquiry) {
      setValidationMessage('Please specify from where did you hear about us.')
      return
    }
    if (!formData.ex_customer) {
      setValidationMessage('Please specify if you are an existing customer.')
      return
    }
    if (!formData.agree) {
      setValidationMessage('Please agree to the terms.')
      return
    }
    if (!isRecaptchaVerified) {
      setValidationMessage('Please verify that you are not a robot.')
      return
    }
    setFormData({
      ...formData,
      timestamp: new Date().toISOString()
    })
    setIsConfirm(true)
  }

  const confirm = async () => {
    sendGAEvent({
      event: 'form_submit',
      category: 'Submit enquiry form',
      action: `Product: ${formData.interested_product}`,
      label: window.location.pathname + window.location.search
    })

    setSubmitDisabled(true)
    // Handle form submission logic

    try {
      const { contact_code, ...rest } = formData

      const payload = {
        ...rest,
        contact_number: `${contact_code}${formData.contact_number}`
      }

      await submitForm(payload)
      setFormData(INIT_FORM_DATA) // Reset form data after successful submission
      setIsError(false)
      setValidationMessage('') // Clear validation message on success
      setSubmitDisabled(false)
      router.push('/personal/enquiry/thank-you/') // Redirect to thank you page
    } catch (error) {
      setErrorMessage(`Error: ${error}`)
      setIsError(true)
      setSubmitDisabled(false)
    }
  }

  const checkConditions = useCallback(() => {
    // const isConsentChecked = formData.agree;
    const submitButton = document.getElementById('submitButton') as HTMLButtonElement | null
    if (submitButton) {
      // Check if conditions to show/hide submit button (if necessary)
      // Not disabling the button as per your requirement
      submitButton.disabled = false // Keep the button enabled
    }
  }, [])

  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js'
    script.async = true
    script.defer = true
    document.body.append(script)

    window.recaptchaCallback = function () {
      setIsRecaptchaVerified(true)
      checkConditions()
    }

    return () => {
      script.remove()
    }
  }, [checkConditions])

  useEffect(() => {
    checkConditions() // Call the function whenever the dependencies change
  }, [checkConditions])

  useEffect(() => {
    setFormRef(ref)
  }, [setFormRef])

  return (
    <>
      <Modal show={!!errorMessage} aria-label="Submission error">
        <Modal.Body>
          <p>{errorMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setErrorMessage('')
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isConfirm} aria-label="Confirm submission">
        <Modal.Body>
          <p>Please review your details before submitting.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="py-2"
            onClick={() => {
              setIsConfirm(false)
            }}
          >
            Close
          </Button>
          <Button
            variant="primary"
            className="py-2"
            onClick={() => {
              setIsConfirm(false)
              confirm()
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!validationMessage} aria-label="Form validation">
        <Modal.Body>
          <p>{validationMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setValidationMessage('')
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <div ref={ref} className="form-container">
        {/* Mobile-only heading. Toggled with CSS (`d-xl-none`), NOT a JS viewport
            check: under `output: 'export'` matchMedia is absent at build time, so a
            JS check renders the desktop branch into the frozen HTML and mismatches
            on hydration for every phone (React #418). `d-xl-none` hides it from
            1200px up, exactly the `maxWidth: 1199` this replaced. LetsConnect
            renders the desktop copy with the mirrored `d-none d-xl-block`. */}
        <div className="col-xl-12 d-xl-none">
          {/* Title */}
          {title !== undefined && title !== null && (
            <h2>{typeof title === 'string' ? (title.trim() ? parse(title) : null) : title}</h2>
          )}

          {/* Subtitle */}
          {description && (
            <p>{typeof description === 'string' ? parse(description) : description}</p>
          )}
        </div>
        <form className="row g-4 app-form-landing" onSubmit={handleSubmit}>
          <div className="col-xl-9 d-flex align-items-center">
            <p className="mb-0 mandatory-text fs-md-16 fs-14">
              Are you an existing alrajhi bank Malaysia customer?
            </p>
          </div>
          <div className="col-xl-3 d-flex align-items-center justify-md-content-end">
            <RadioGroup row name="ex_customer" value={formData.ex_customer} onChange={handleChange}>
              <FormControlLabel
                value="yes"
                className="customer-radio-group"
                control={<Radio />}
                label="Yes"
                data-translate="forms_9"
                sx={{
                  mr: 2,
                  color: 'var(--grey-500)',
                  alignItems: 'center'
                }}
              />

              <FormControlLabel
                value="no"
                className="customer-radio-group"
                control={<Radio />}
                label="No"
                data-translate="forms_10"
                sx={{
                  color: 'var(--grey-500)',
                  alignItems: 'center'
                }}
              />
            </RadioGroup>
          </div>

          <div className="col-xl-6">
            <label className="form-label" htmlFor="salutation">
              <span className="form-label-required">*</span>
              <span data-translate="forms_2">Salutation</span>
            </label>

            <FormControl fullWidth>
              <Select
                className="custom-mui-select"
                id="salutation"
                name="salutation"
                IconComponent={CustomArrow}
                value={formData.salutation}
                displayEmpty
                onChange={e => {
                  handleChange(e)
                  sendFormStartGA()
                }}
                MenuProps={{
                  slotProps: { paper: { className: 'custom-mui-dropdown' } }
                }}
                renderValue={selected => {
                  if (!selected) {
                    return <span className="placeholder-text">--Select--</span>
                  }

                  return (
                    salutationOptions.find(option => option.value === selected)?.text ?? selected
                  )
                }}
              >
                <MenuItem value="" disabled>
                  --Select--
                </MenuItem>

                {salutationOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="col-xl-6">
            <label className="form-label" htmlFor="name">
              <span className="form-label-required">*</span>
              <span data-translate="forms_3">Name</span>
            </label>

            <TextField
              fullWidth
              id="name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              variant="outlined"
              className="custom-mui-input"
            />
          </div>

          <div className="form-group col-xl-6">
            <label className="form-label" htmlFor="number">
              <span>Contact number (Malaysia only)</span>
            </label>

            <TextField
              fullWidth
              id="number"
              name="contact_number"
              placeholder="0123456789"
              slotProps={{ htmlInput: { maxLength: 15 } }}
              required
              value={formData.contact_number}
              onChange={handleChange}
              className="custom-mui-input"
            />
          </div>

          <div className="col-xl-6">
            <label htmlFor="email" className="form-label">
              <span className="form-label-required">*</span>
              <span data-translate="forms_5">Email</span>
            </label>

            <TextField
              fullWidth
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="custom-mui-input"
              required
            />
          </div>

          <div className="col-12">
            <label className="form-label" htmlFor="preferred_branch">
              <span className="form-label-required">*</span>
              <span data-translate="forms_11">Preferred branch or Smart Financial Center</span>
            </label>

            <FormControl fullWidth>
              <Select
                className="custom-mui-select"
                id="preferred_branch"
                name="preferred_branch"
                IconComponent={CustomArrow}
                value={formData.preferred_branch}
                displayEmpty
                onChange={e =>
                  setFormData({
                    ...formData,
                    preferred_branch: e.target.value
                  })
                }
                MenuProps={{
                  slotProps: { paper: { className: 'custom-mui-dropdown' } }
                }}
                renderValue={selected => {
                  if (!selected) {
                    return <span className="placeholder-text">--Select--</span>
                  }
                  return selected
                }}
              >
                <MenuItem value="" disabled>
                  --Select--
                </MenuItem>

                {branchOptions.map(branch => (
                  <MenuItem key={branch} value={branch}>
                    {branch}
                  </MenuItem>
                ))}

                <MenuItem value="No Preference">
                  I don't have a preferred bank branch or Smart Financial Center
                </MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className="col-xl-6">
            <label className="form-label" htmlFor="interested_product">
              <span className="form-label-required">*</span>
              <span data-translate="forms_6">I am interested in</span>
            </label>

            <FormControl fullWidth>
              <Select
                className="custom-mui-select"
                id="interested_product"
                name="interested_product"
                IconComponent={CustomArrow}
                value={formData.interested_product}
                displayEmpty
                required
                onChange={handleChange}
                MenuProps={{
                  slotProps: { paper: { className: 'custom-mui-dropdown' } }
                }}
                renderValue={selected => {
                  if (!selected) {
                    return <span className="placeholder-text">--Select--</span>
                  }

                  const products = tab !== 1 ? personalOptions : businessOptions
                  const selectedProduct = products.find(item => item.value === selected)

                  return selectedProduct?.text || selected
                }}
              >
                {tab !== 1 &&
                  personalOptions.map(item => (
                    <MenuItem key={item.value} value={item.value} disabled={item.disabled}>
                      {item.text}
                    </MenuItem>
                  ))}

                {tab === 1 &&
                  businessOptions.map(item => (
                    <MenuItem
                      key={`${item.text}-${item.value}`}
                      value={item.value}
                      disabled={item.disabled}
                      className={
                        item.disabled && item.text !== '-- Select --' ? 'dropdown-category' : ''
                      }
                    >
                      {item.text}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>

          <div className="col-xl-6">
            <label className="form-label" htmlFor="preferredContactMethod">
              <span className="form-label-required">*</span>
              <span data-translate="forms_6">I prefer to be contacted</span>
            </label>

            <FormControl fullWidth>
              <Select
                className="custom-mui-select"
                id="preferredContactMethod"
                IconComponent={CustomArrow}
                value={formData.preferred_contact_time || ''}
                displayEmpty
                onChange={e =>
                  setFormData({
                    ...formData,
                    preferred_contact_time: e.target.value
                  })
                }
                MenuProps={{
                  slotProps: { paper: { className: 'custom-mui-dropdown' } }
                }}
                renderValue={selected => {
                  if (!selected) {
                    return <span className="placeholder-text">--Select--</span>
                  }

                  return (
                    contactTimeOptions.find(option => option.value === selected)?.text ?? selected
                  )
                }}
              >
                <MenuItem value="" disabled>
                  --Select--
                </MenuItem>

                {contactTimeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="col-xl-6">
            <label className="form-label" htmlFor="preferredReachMethod">
              <span className="form-label-required">*</span>
              <span data-translate="forms_7">I prefer to be reached via</span>
            </label>

            <FormControl fullWidth>
              <Select
                className="custom-mui-select"
                id="preferredReachMethod"
                IconComponent={CustomArrow}
                value={formData.preferred_reach_method || ''}
                displayEmpty
                onChange={e =>
                  setFormData({
                    ...formData,
                    preferred_reach_method: e.target.value
                  })
                }
                MenuProps={{
                  slotProps: { paper: { className: 'custom-mui-dropdown' } }
                }}
                renderValue={selected => {
                  if (!selected) {
                    return <span className="placeholder-text">--Select--</span>
                  }

                  return (
                    reachMethodOptions.find(option => option.value === selected)?.text ?? selected
                  )
                }}
              >
                <MenuItem value="" disabled>
                  --Select--
                </MenuItem>

                {reachMethodOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="col-xl-6">
            <label className="form-label" htmlFor="source_of_enquiry">
              <span className="form-label-required">*</span>
              <span data-translate="forms_6">How did you hear about us?</span>
            </label>

            <FormControl fullWidth>
              <Select
                className="custom-mui-select"
                id="source_of_enquiry"
                name="source_of_enquiry"
                IconComponent={CustomArrow}
                value={formData.source_of_enquiry}
                displayEmpty
                required
                onChange={handleChange}
                MenuProps={{
                  slotProps: { paper: { className: 'custom-mui-dropdown' } }
                }}
                renderValue={selected => {
                  if (!selected) {
                    return <span className="placeholder-text">--Select--</span>
                  }

                  const selectedOption = sourceOptions.find(item => item.value === selected)

                  return selectedOption ? parse(selectedOption.text) : selected
                }}
              >
                {sourceOptions.map(item => (
                  <MenuItem key={item.value} value={item.value} disabled={item.disabled}>
                    {parse(item.text)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="col-xl-12">
            <label className="form-label" htmlFor="comments">
              <span>Leave your questions or comments here</span>
            </label>

            <TextField
              fullWidth
              multiline
              id="comments"
              value={formData.comments || ''}
              onChange={e =>
                setFormData({
                  ...formData,
                  comments: e.target.value
                })
              }
              className="custom-mui-textarea"
            />
          </div>

          <div className="col-xl-12">
            <p className="mandatory-text fs-md-16 fs-14">
              (<span className="form-label-required">*</span>) Mandatory fields
            </p>
          </div>

          <div
            className="g-recaptcha"
            data-sitekey="6Ld7zSEUAAAAAPQIdyQzYIfZsn8eAEkODcwGa-iY"
            data-callback="recaptchaCallback"
            style={{ marginTop: 0 }}
          ></div>

          <div className="row mt-3">
            <div className="col-12">
              <FormControlLabel
                className="custom-checkbox-label"
                control={
                  <Checkbox
                    id="consent"
                    name="agree"
                    checked={formData.agree}
                    onChange={handleChange}
                  />
                }
                label={
                  <p
                    id="consent-text"
                    className="consent-text fs-md-16 fs-14"
                    data-translate="forms_12"
                  >
                    I hereby agree and consent that Al Rajhi Banking &amp; Investment Corporation
                    (Malaysia) Bhd may collect, use and process my personal information set out in
                    my application, and that I have read/understood and agreed to the contents of
                    the{' '}
                    <Link
                      href="/about/corporate-governance/compliance"
                      style={{ color: 'var(--alrajhi-primary)' }}
                    >
                      Personal Data Protection Notice.
                    </Link>
                  </p>
                }
              />
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-12">
              <FormControlLabel
                className="custom-checkbox-label"
                control={
                  <Checkbox
                    id="marketingConsent"
                    name="marketing_consent"
                    checked={formData.marketing_consent}
                    onChange={handleChange}
                    aria-label="Marketing Consent"
                  />
                }
                label={
                  <p id="marketing-consent-text" className="consent-text fs-md-16 fs-14">
                    I consent to Al Rajhi Banking &amp; Investment Corporation (Malaysia) Bhd
                    processing my personal data to send me promotional and marketing materials
                    regarding financial products, services, and upcoming campaigns.
                  </p>
                }
              />
            </div>
          </div>

          <div className="col-12 col-md-5 d-grid gap-2">
            <PrimaryButton
              text="Submit"
              disabled={submitDisabled}
              onClick={handleSubmit}
              id="submitButton"
            />
          </div>
        </form>
      </div>
    </>
  )
}

export default EnquiryForm
